import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { TrustBadge } from '../components/TrustBadge';
import {
  ArrowLeft, Gavel, Clock, TrendingUp, User, Shield, Star, Package, AlertTriangle, Pencil
} from 'lucide-react';
import { PhotoGallery } from '../components/PhotoGallery';
import { AuthenticityPanel } from '../components/AuthenticityBadge';
import { NftTrustPanel } from '../components/NftTrustBadge';
import { ListingActions, ListingMeta } from '../components/ListingActions';
import EditListingDialog from '../components/EditListingDialog';

const tg = () => window.Telegram?.WebApp;
const haptic = (type = 'impact', style = 'light') => {
  try { if (type === 'impact') tg()?.HapticFeedback?.impactOccurred(style); } catch (e) {}
};

function AuctionRoom() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, updateBalance, loading: authLoading } = useAuth();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [hostTrust, setHostTrust] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [bidLoading, setBidLoading] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/'); return; }
    fetchAuction();
    const interval = setInterval(fetchAuction, 10000);
    return () => clearInterval(interval);
  }, [auctionId, isAuthenticated, authLoading]);

  useEffect(() => {
    if (!auction) return;
    const tick = setInterval(() => {
      const now = new Date();
      const ends = new Date(auction.ends_at);
      const diff = ends - now;
      if (diff <= 0) { setCountdown('ENDED'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    }, 1000);
    return () => clearInterval(tick);
  }, [auction]);

  const fetchAuction = async () => {
    try {
      const response = await axios.get(`${API}/auctions/${auctionId}`);
      setAuction(response.data.auction);
      setBids(response.data.bids || []);
      setHostTrust(response.data.host_trust);
    } catch (e) {
      toast.error('Failed to load auction');
    }
  };

  const handleBid = async () => {
    const amount = parseFloat(bidAmount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    setBidLoading(true);
    try {
      const response = await axios.post(`${API}/auctions/${auctionId}/bid`, { amount });
      haptic('notification', 'success');
      toast.success(response.data.message);
      updateBalance(response.data.new_balance);
      setShowBidDialog(false);
      setBidAmount('');
      fetchAuction();
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Bid failed');
    } finally {
      setBidLoading(false);
    }
  };

  if (!auction) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto"></div>
      </div>
    );
  }

  const minBid = auction.current_bid === 0 ? auction.starting_bid : auction.current_bid + auction.bid_increment;
  const isEnded = auction.status !== 'active' || countdown === 'ENDED';
  const isWinning = auction.current_bidder_id === user?.user_id;
  const balance = user?.balance || 0;

  return (
    <div className="min-h-screen pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3 flex items-center gap-3">
          <button data-testid="back-button" onClick={() => navigate('/')} className="text-slate-400 p-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-white leading-tight flex items-center gap-2">
              <Gavel className="w-4 h-4 text-purple-400" /> Auction
            </h1>
          </div>
          {/* Timer */}
          <div className={`px-2.5 py-1 rounded-lg text-xs font-bold font-mono ${
            isEnded ? 'bg-slate-700 text-slate-400' :
            countdown.includes('s') && !countdown.includes('m') ? 'bg-red-500/20 text-red-400 animate-pulse' :
            'bg-purple-500/15 text-purple-300'
          }`}>
            <Clock className="w-3 h-3 inline mr-1" />{countdown || '...'}
          </div>
        </div>
      </header>

      {/* Auction Info */}
      <div className="px-4 pt-4 space-y-3">
        {auction.photo_urls && auction.photo_urls.length > 0 && (
          <PhotoGallery photos={auction.photo_urls} alt={auction.room_name} />
        )}
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p data-testid="auction-name" className="text-lg font-bold text-white">{auction.room_name}</p>
              <p className="text-xs text-slate-400 mt-0.5">{auction.prize_description}</p>
            </div>
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${
              auction.prize_type === 'nft' ? 'bg-purple-500/15 text-purple-300 border border-purple-500/25' :
              'bg-blue-500/15 text-blue-300 border border-blue-500/25'
            }`}>{auction.prize_type}</span>
          </div>

          {/* Host info */}
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-700/40">
            <User className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs text-slate-400">Host: <span className="text-white font-medium">{auction.host_username}</span></span>
            {hostTrust && <TrustBadge trust={hostTrust} />}
          </div>

          {/* Current bid */}
          <div className="text-center py-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">
              {auction.current_bid > 0 ? 'Current Bid' : 'Starting Bid'}
            </p>
            <p data-testid="current-bid" className="text-3xl font-black text-orange-400 font-mono">
              {(auction.current_bid || auction.starting_bid).toFixed(2)} <span className="text-base text-slate-400">XRP</span>
            </p>
            {auction.current_bidder_username && (
              <p className="text-[11px] text-slate-500 mt-1">
                by <span className={`font-medium ${isWinning ? 'text-emerald-400' : 'text-white'}`}>
                  {isWinning ? 'You' : auction.current_bidder_username}
                </span>
              </p>
            )}
          </div>

          {/* Bid stats */}
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="text-center bg-slate-900/40 rounded-lg py-2">
              <p className="text-xs font-bold text-white">{auction.bid_count}</p>
              <p className="text-[9px] text-slate-500">Bids</p>
            </div>
            <div className="text-center bg-slate-900/40 rounded-lg py-2">
              <p className="text-xs font-bold text-white">{auction.bid_increment} XRP</p>
              <p className="text-[9px] text-slate-500">Min Increment</p>
            </div>
            <div className="text-center bg-slate-900/40 rounded-lg py-2">
              <p className="text-xs font-bold text-white">{auction.prize_value_xrp}</p>
              <p className="text-[9px] text-slate-500">Est. Value</p>
            </div>
          </div>
        </div>

        {/* Authenticity Panel */}
        <AuthenticityPanel
          condition={auction.condition}
          grade={auction.grade}
          certNumber={auction.cert_number}
          verified={auction.authenticity_verified}
        />

        {/* NFT trust analysis */}
        {auction.prize_type === 'nft' && auction.nft_analysis && (
          <NftTrustPanel analysis={auction.nft_analysis} />
        )}

        {/* Marketplace meta + actions */}
        <ListingMeta
          createdAt={auction.created_at}
          location={auction.host_location}
          prizeType={auction.prize_type}
        />
        <ListingActions
          shareUrl={typeof window !== 'undefined' ? window.location.href : ''}
          shareTitle={`${auction.room_name} — ${auction.prize_description} · Bid on Vitamin C Gainz`}
          hostUsername={auction.host_username}
          hostTelegramUsername={auction.host_telegram_username}
          hostUserId={auction.host_user_id}
          targetType="auction"
          targetId={auction.auction_id}
          targetName={auction.room_name}
          isOwnListing={user?.user_id === auction.host_user_id}
          onBlocked={() => navigate('/')}
        />
      </div>

      {/* Host Edit button — only active auctions, host only */}
      {user?.user_id === auction.host_user_id && auction.status === 'active' && (
        <div className="px-4 pt-2">
          <button
            data-testid="host-edit-auction-button"
            onClick={() => { haptic('impact', 'light'); setShowEditDialog(true); }}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 py-2 text-xs font-bold text-amber-300 transition active:scale-[0.98]"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit Listing
          </button>
        </div>
      )}

      {/* Bid Button */}
      {!isEnded && (
        <div className="px-4 pt-3">
          {isWinning ? (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
              <p className="text-sm font-bold text-emerald-400">You're the highest bidder!</p>
              <p className="text-[10px] text-slate-500">You'll win if no one outbids you</p>
            </div>
          ) : (
            <Button
              data-testid="place-bid-button"
              onClick={() => { haptic('impact', 'medium'); setBidAmount(minBid.toFixed(2)); setShowBidDialog(true); }}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 h-12 text-base font-bold rounded-xl"
            >
              <Gavel className="w-4 h-4 mr-2" /> Place Bid (min {minBid.toFixed(2)} XRP)
            </Button>
          )}
        </div>
      )}

      {/* Ended status */}
      {isEnded && (
        <div className="px-4 pt-3">
          <div className={`rounded-xl p-4 text-center ${
            auction.status === 'completed' ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-slate-800/50 border border-slate-700'
          }`}>
            {auction.status === 'completed' ? (
              <>
                <p className="text-sm font-bold text-emerald-400">Auction Won!</p>
                <p className="text-xs text-slate-400 mt-1">Winner: {auction.winner_username || auction.current_bidder_username || 'Anon'} for {auction.final_bid || auction.current_bid} XRP</p>
              </>
            ) : (
              <p className="text-sm font-bold text-slate-400">{auction.result_note || 'Auction ended'}</p>
            )}
          </div>
        </div>
      )}

      {/* Reserve notice */}
      {auction.reserve_price && auction.current_bid < auction.reserve_price && !isEnded && (
        <div className="px-4 pt-2">
          <div className="flex items-center gap-2 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[10px] text-amber-300">Reserve price not yet met</span>
          </div>
        </div>
      )}

      {/* Bid History */}
      <div className="px-4 pt-4">
        <p className="text-xs font-bold text-white mb-2.5">Bid History ({bids.length})</p>
        {bids.length === 0 ? (
          <p className="text-[11px] text-slate-600 text-center py-4">No bids yet. Be the first!</p>
        ) : (
          <div className="space-y-1.5">
            {bids.map((bid, idx) => (
              <div key={bid.bid_id} data-testid={`bid-${idx}`} className="flex items-center justify-between bg-slate-800/30 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold ${
                    idx === 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-700 text-slate-400'
                  }`}>{idx + 1}</span>
                  <span className="text-xs text-white">{bid.bidder_username}</span>
                </div>
                <span className="text-xs font-bold text-orange-300">{bid.amount.toFixed(2)} XRP</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bid Dialog */}
      <Dialog open={showBidDialog} onOpenChange={setShowBidDialog}>
        <DialogContent data-testid="bid-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Place Bid</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">{auction.room_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="bg-slate-800/50 rounded-lg p-3 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Current bid</span>
                <span className="text-white font-medium">{(auction.current_bid || 0).toFixed(2)} XRP</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Min bid</span>
                <span className="text-orange-300 font-medium">{minBid.toFixed(2)} XRP</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Your balance</span>
                <span className="text-slate-300">{balance.toFixed(2)} XRP</span>
              </div>
            </div>
            <Input
              data-testid="bid-amount-input"
              type="number"
              step="0.01"
              placeholder={`Min ${minBid.toFixed(2)} XRP`}
              value={bidAmount}
              onChange={(e) => setBidAmount(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white h-11 text-lg font-mono text-center"
            />
            {parseFloat(bidAmount) > balance && (
              <p className="text-[11px] text-red-400 text-center">Insufficient balance</p>
            )}
            <Button
              data-testid="confirm-bid-button"
              onClick={handleBid}
              disabled={bidLoading || parseFloat(bidAmount) < minBid || parseFloat(bidAmount) > balance}
              className="w-full bg-purple-600 hover:bg-purple-700 h-11 font-bold text-base"
            >
              {bidLoading ? 'Placing...' : `Bid ${parseFloat(bidAmount || 0).toFixed(2)} XRP`}
            </Button>
            <p className="text-[9px] text-slate-600 text-center">Your XRP is held until you're outbid or you win.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Host Edit Dialog */}
      <EditListingDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        kind="auction"
        listing={auction}
        onUpdated={(fresh) => { if (fresh) setAuction(fresh); }}
      />
    </div>
  );
}

export default AuctionRoom;
